import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const villeneuveLoubet: ChauffeurPage = {
  slug: "villeneuve-loubet",
  title: "Chauffeur privé à Villeneuve-Loubet – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Villeneuve-Loubet : transferts aéroport de Nice, Marina Baie des Anges, plages et vieux village, 7j/7.",
  keywords: [
    "chauffeur privé villeneuve-loubet",
    "vtc villeneuve-loubet",
    "chauffeur villeneuve-loubet",
    "vtc marina baie des anges",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Villeneuve-Loubet",
    subtitle:
      "Transferts aéroport de Nice, Marina Baie des Anges, plages et vieux village d’Escoffier — 24h/24, 7j/7.",
    keywords: [
      "VTC Villeneuve-Loubet",
      "chauffeur Marina Baie des Anges",
      "transfert aéroport Nice",
      "chauffeur Villeneuve-Loubet plage",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Chauffeur privé & VTC à Villeneuve-Loubet et Marina Baie des Anges",
    badges: ["Marina Baie des Anges", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Villeneuve-Loubet",
    items: [
      {
        title: "Marina Baie des Anges",
        description:
          "Port de plaisance et immeubles en arc face à la mer : dépose au pied des résidences et du port, sans chercher à se garer.",
      },
      {
        title: "Vieux village & Escoffier",
        description:
          "Village perché, château à donjon et Musée Escoffier de l’Art Culinaire : dépose au plus près des ruelles.",
      },
      {
        title: "Aéroport de Nice en ~15 min",
        description:
          "Liaison directe par l’A8 ou le bord de mer, avec suivi de vol et accueil personnalisé.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Villeneuve-Loubet",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Villeneuve-Loubet ⇄ aéroport de Nice Côte d’Azur en ~15 min, avec suivi de vol et accueil aux terminaux.",
      },
      {
        title: "Marina Baie des Anges",
        text: "Dessertes du port de plaisance, des résidences en arc et des restaurants du bord de mer.",
      },
      {
        title: "Vieux village & Musée Escoffier",
        text: "Dépose au pied du village perché, visite du château et du Musée Escoffier de l’Art Culinaire.",
      },
      {
        title: "Plages & Parc de Vaugrenier",
        text: "Journées en famille entre plages de Villeneuve-Loubet et parc départemental de Vaugrenier.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur dédié pour vos déplacements sur la Côte d’Azur.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      {
        name: "Marina Baie des Anges",
        description: "Port de plaisance et immeubles en arc signés Minangoy.",
      },
      {
        name: "Villeneuve-Loubet-Plage",
        description: "Front de mer, plages et promenade.",
      },
      {
        name: "Vieux village",
        description: "Village perché, château médiéval à donjon pentagonal.",
      },
      {
        name: "Musée Escoffier de l’Art Culinaire",
        description:
          "Maison natale d’Auguste Escoffier, au cœur du vieux village.",
      },
      {
        name: "Parc de Vaugrenier",
        description: "Grand parc départemental boisé, proche du littoral.",
      },
      {
        name: "Aéroport de Nice",
        description: "~15 min par l’A8 ou le bord de mer.",
      },
    ],
  },
  generic: {
    heading: "Villeneuve-Loubet, du vieux village à la Marina Baie des Anges",
    subheading:
      "Entre plages, port de plaisance et village perché d’Escoffier, un chauffeur pour les deux Villeneuve.",
    paragraphs: [
      "Villeneuve-Loubet s’étire entre mer et collines : sur le littoral, la Marina Baie des Anges déploie ses immeubles en arc et son port de plaisance, bordés de plages et de restaurants ; dans les terres, le vieux village perché veille sous son château médiéval au donjon pentagonal.",
      "C’est là, dans le vieux village, que naquit Auguste Escoffier : le Musée Escoffier de l’Art Culinaire lui rend hommage. Le chauffeur vous dépose au plus près des ruelles, puis vous reprend au pied du village pour rejoindre la plage ou le parc de Vaugrenier.",
      "À ~15 min de l’aéroport de Nice par l’A8 ou le bord de mer, Villeneuve-Loubet est idéale pour un transfert rapide comme pour une journée en famille — le secteur de Marineland et les grandes plages voisines sont à quelques minutes.",
    ],
    bullets: [
      "Port & plages de la Marina",
      "Vieux village & Musée Escoffier",
      "Aéroport de Nice à ~15 min",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Villeneuve-Loubet",
    steps: [
      "Indiquez votre trajet ou vos adresses (aéroport, Marina, vieux village, plages).",
      "Nous confirmons le véhicule adapté et le forfait ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Dépose au port, au pied du village ou sur le front de mer, reprise sur demande.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Villeneuve-Loubet"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Villeneuve-Loubet ?",
      a: "Comptez environ 15 minutes par l’A8 ou le bord de mer hors heures de pointe. Nous suivons votre vol pour ajuster l’heure de prise en charge, dans les deux sens.",
    },
    {
      q: "Desservez-vous la Marina Baie des Anges et son port ?",
      a: "Oui, nous déposons et reprenons au pied des immeubles en arc, au port de plaisance et le long des plages de Villeneuve-Loubet, sans que vous ayez à chercher une place.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et les week-ends d’affluence ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Villeneuve-Loubet ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Peut-on rejoindre le vieux village et le Musée Escoffier en voiture ?",
      a: "Oui. Le chauffeur vous dépose au plus près du vieux village perché ; les ruelles autour du château et du Musée Escoffier de l’Art Culinaire se poursuivent à pied, et il vous reprend au même endroit.",
    },
  ],
  relatedLinks: [
    { label: "VTC aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "Chauffeur privé à Nice", href: "/chauffeur/nice" },
    { label: "Transferts sur la Côte d’Azur", href: "/chauffeur/cote-d-azur" },
    { label: "Service de chauffeur à Cannes", href: "/chauffeur/cannes" },
  ],
  cta: {
    title: "Votre chauffeur privé à Villeneuve-Loubet",
    text: "Marina Baie des Anges, vieux village ou transfert aéroport — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Villeneuve-Loubet — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Villeneuve-Loubet : transfert aéroport de Nice (~15 min), Marina Baie des Anges, plages et vieux village d’Escoffier.",
    canonical: "/chauffeur/villeneuve-loubet",
  },
};

export default villeneuveLoubet;
